import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledInput,
    Form,
    H5Regular,
    useToastController,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { type MembershipDetails } from '../../entities/entityTypes';
import { useRefetchMembershipDetailsMutationOptions } from '../../entities/membership/hooks/useRefetchMembershipDetailsMutationOptions';
import { useAddPartnerToMembership } from '../hooks/useAddPartnerToMembership';

const addPartnerSchema = z.object({
    email: z
        .string()
        .min(1, 'Email is required')
        .email('Please enter a valid email address'),
});

type AddPartnerSchema = z.infer<typeof addPartnerSchema>;

interface AddPartnerModalProps {
    isOpen: boolean;
    onClose: () => void;
    membershipDetails: MembershipDetails;
}

export const AddPartnerModal = ({
    isOpen,
    onClose,
    membershipDetails,
}: AddPartnerModalProps) => {
    const { users } = membershipDetails;
    if (users.length > 1) return;

    const primaryUser = users[0];
    if (!primaryUser) return;

    const toast = useToastController();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const refetchOptions =
        useRefetchMembershipDetailsMutationOptions(membershipDetails);
    const { mutateAsync: addPartnerToMembership } =
        useAddPartnerToMembership(refetchOptions);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<AddPartnerSchema>({
        resolver: zodResolver(addPartnerSchema),
        defaultValues: {
            email: '',
        },
    });

    const onSubmit: SubmitHandler<AddPartnerSchema> = async ({ email }) => {
        setIsSubmitting(true);

        try {
            await addPartnerToMembership({
                urlParams: { id: primaryUser.id },
                body: { partnerEmail: email },
            });

            toast.show(
                'Sucessfully added partner to membership',
                MutationSuccessToastOptions,
            );

            onClose();
        } catch (e) {
            const messageFromError = e instanceof Error ? e.message : String(e);
            toast.show(
                'Error adding partner to membership',
                MutationErrorToastOptions(messageFromError),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <GroveSimpleModal
                isOpen={isOpen}
                hasCloseButton={true}
                onClose={() => {
                    onClose();
                }}
                renderInAppRoot={false}
            >
                <YStack
                    marginHorizontal={16}
                    marginTop={32}
                    width={500}
                    gap={24}
                    marginBottom={24}
                >
                    <H5Regular>Add Partner to Membership</H5Regular>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <YStack gap={20}>
                            <ControlledInput
                                name="email"
                                control={control}
                                register={register}
                                errors={errors}
                                label="Partner Email"
                                disabled={isSubmitting}
                            />
                            <View alignSelf="flex-end">
                                <Button
                                    disabled={!isValid || isSubmitting}
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.TINY}
                                    text="Add Partner"
                                    onPress={handleSubmit(onSubmit)}
                                />
                            </View>
                        </YStack>
                    </Form>
                </YStack>
            </GroveSimpleModal>
        </>
    );
};
